
import { supabase } from '@/integrations/supabase/client';
import { Client } from '@/types';
import { UseToastReturn } from '@/hooks/toast-types';

export const fetchClients = async (toast: UseToastReturn['toast']): Promise<Client[]> => {
  try {
    const { data, error } = await supabase
      .from('clients')
      .select('*')
      .order('name');

    if (error) {
      throw error;
    }

    return data as Client[];
  } catch (error) {
    console.error('Error fetching clients:', error);
    toast({
      title: 'Error',
      description: 'Failed to fetch clients',
      variant: 'destructive',
    });
    return [];
  }
};

export const createClient = async (
  name: string, 
  toast: UseToastReturn['toast'],
  refreshClients: () => Promise<void>
): Promise<string | null> => {
  try {
    const { data, error } = await supabase
      .from('clients')
      .insert({ name })
      .select()
      .single();

    if (error) {
      throw error;
    }

    toast({
      title: 'Success',
      description: `Client "${name}" created successfully`,
    });

    // Refresh clients list
    await refreshClients();
    return data.id;
  } catch (error) {
    console.error('Error creating client:', error);
    toast({
      title: 'Error',
      description: 'Failed to create client',
      variant: 'destructive',
    });
    return null;
  }
};

export const updateClient = async (
  id: string, 
  name: string, 
  toast: UseToastReturn['toast'],
  refreshClients: () => Promise<void>
): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('clients')
      .update({ name, updated_at: new Date().toISOString() })
      .eq('id', id);

    if (error) {
      throw error;
    }

    toast({
      title: 'Success',
      description: `Client updated successfully`,
    });

    // Refresh clients list
    await refreshClients();
    return true;
  } catch (error) {
    console.error('Error updating client:', error);
    toast({
      title: 'Error',
      description: 'Failed to update client',
      variant: 'destructive',
    });
    return false;
  }
};

export const deleteClient = async (
  id: string, 
  toast: UseToastReturn['toast'],
  refreshClients: () => Promise<void>
): Promise<boolean> => {
  // Don't allow deleting the default client
  if (id === '00000000-0000-0000-0000-000000000000') {
    toast({
      title: 'Cannot Delete',
      description: 'The default client cannot be deleted',
      variant: 'destructive',
    });
    return false;
  }

  try {
    const { error } = await supabase
      .from('clients')
      .delete()
      .eq('id', id);

    if (error) {
      throw error;
    }

    toast({
      title: 'Success',
      description: 'Client deleted successfully',
    });

    // Refresh clients list
    await refreshClients();
    return true;
  } catch (error) {
    console.error('Error deleting client:', error);
    toast({
      title: 'Error',
      description: 'Failed to delete client',
      variant: 'destructive',
    });
    return false;
  }
};
